/**
 * Custom hook for managing presence data and API calls
 */

import { useState, useEffect, useCallback } from 'react';
import { usePresenceAnalyticsService } from '@actschurch/ccms-sdk';
import type {
  SystemPresenceStats,
  UserActivitySummary,
  UserPresenceStats,
  PresenceTimelineEntry,
  TimeRange,
} from '../types';

export function usePresenceData() {
  const presenceService = usePresenceAnalyticsService();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [systemStats, setSystemStats] = useState<SystemPresenceStats | null>(null);
  const [mostActiveUsers, setMostActiveUsers] = useState<UserActivitySummary[]>([]);
  const [selectedUserStats, setSelectedUserStats] = useState<UserPresenceStats | null>(null);
  const [selectedUserTimeline, setSelectedUserTimeline] = useState<PresenceTimelineEntry[]>([]);

  const getDateRange = useCallback((range: TimeRange): { startDate: string; endDate: string } => {
    const endDate = new Date();
    let startDate = new Date();

    switch (range) {
      case '7d':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(endDate.getDate() - 90);
        break;
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  }, []);

  const fetchSystemStats = useCallback(
    async (timeRange: TimeRange) => {
      try {
        setLoading(true);
        setError(null);
        const { startDate, endDate } = getDateRange(timeRange);

        const stats = await presenceService.getSystemStats(
          new Date(startDate),
          new Date(endDate)
        );

        if (stats) {
          setSystemStats(stats);
        }
      } catch (err: any) {
        console.error('[usePresenceData] Failed to fetch system stats:', err);
        setError(err?.message || 'Failed to load system statistics');
      } finally {
        setLoading(false);
      }
    },
    [presenceService, getDateRange]
  );

  const fetchMostActiveUsers = useCallback(
    async (timeRange: TimeRange) => {
      try {
        const { startDate, endDate } = getDateRange(timeRange);

        const users = await presenceService.getMostActiveUsers(
          new Date(startDate),
          new Date(endDate),
          50
        );

        if (users) {
          setMostActiveUsers(users);
        }
      } catch (err: any) {
        console.error('[usePresenceData] Failed to fetch active users:', err);
      }
    },
    [presenceService, getDateRange]
  );

  const fetchUserDetails = useCallback(
    async (userId: string, timeRange: TimeRange = '30d') => {
      try {
        setLoading(true);
        setError(null);
        const { startDate, endDate } = getDateRange(timeRange);

        const [stats, timeline] = await Promise.all([
          presenceService.getUserStats(
            userId,
            new Date(startDate),
            new Date(endDate)
          ),
          presenceService.getUserTimeline(
            userId,
            new Date(startDate),
            new Date(endDate)
          ),
        ]);

        if (stats) {
          setSelectedUserStats(stats);
        }

        if (timeline) {
          setSelectedUserTimeline(timeline);
        }
      } catch (err: any) {
        console.error('[usePresenceData] Failed to fetch user details:', err);
        setError(err?.message || 'Failed to load user details');
      } finally {
        setLoading(false);
      }
    },
    [presenceService, getDateRange]
  );

  const clearUserDetails = useCallback(() => {
    setSelectedUserStats(null);
    setSelectedUserTimeline([]);
  }, []);

  return {
    loading,
    error,
    systemStats,
    mostActiveUsers,
    selectedUserStats,
    selectedUserTimeline,
    fetchSystemStats,
    fetchMostActiveUsers,
    fetchUserDetails,
    clearUserDetails,
  };
}
